<template>
  <!-- Overlay -->
  <div v-if="isOpen" ref="overlayRef" class="drawer-overlay" @click="closeDrawer"></div>

  <!-- Drawer -->
  <div
    v-if="isOpen"
    ref="drawerRef"
    class="drawer"
    :class="[`drawer--${position}`, drawerClass]"
    :style="drawerStyles"
  >
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { gsap } from 'gsap';

interface Props {
  isOpen: boolean;
  position?: 'left' | 'right';
  width?: string | number;
  height?: string | number;
  drawerClass?: string;
  overlayClass?: string;
  animationDuration?: number;
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void;
  (e: 'close'): void;
  (e: 'open'): void;
}

const props = withDefaults(defineProps<Props>(), {
  position: 'left',
  width: '300px',
  height: '100vh',
  animationDuration: 0.4,
});

const emit = defineEmits<Emits>();

const drawerRef = ref<HTMLElement>();
const overlayRef = ref<HTMLElement>();

const drawerStyles = computed(() => ({
  width: typeof props.width === 'number' ? `${props.width}px` : props.width,
  height: typeof props.height === 'number' ? `${props.height}px` : props.height,
}));

const closeDrawer = () => {
  emit('update:isOpen', false);
  emit('close');
};

const animateIn = async () => {
  await nextTick();

  if (drawerRef.value && overlayRef.value) {
    // Set initial state
    gsap.set(overlayRef.value, { opacity: 0 });

    if (props.position === 'left') {
      gsap.set(drawerRef.value, { x: '-100%' });
    } else {
      gsap.set(drawerRef.value, { x: '100%' });
    }

    // Animate in
    const tl = gsap.timeline();

    tl.to(overlayRef.value, {
      opacity: 1,
      duration: props.animationDuration,
      ease: 'power2.out',
    });

    tl.to(
      drawerRef.value,
      {
        x: 0,
        duration: props.animationDuration,
        ease: 'power2.out',
      },
      0
    );

    emit('open');
  }
};

const animateOut = async () => {
  if (drawerRef.value && overlayRef.value) {
    const tl = gsap.timeline();

    tl.to(overlayRef.value, {
      opacity: 0,
      duration: props.animationDuration,
      ease: 'power2.in',
    });

    if (props.position === 'left') {
      tl.to(
        drawerRef.value,
        {
          x: '-100%',
          duration: props.animationDuration,
          ease: 'power2.in',
        },
        0
      );
    } else {
      tl.to(
        drawerRef.value,
        {
          x: '100%',
          duration: props.animationDuration,
          ease: 'power2.in',
        },
        0
      );
    }
  }
};

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      animateIn();
    } else {
      animateOut();
    }
  }
);

onMounted(() => {
  if (props.isOpen) {
    animateIn();
  }
});
</script>

<style scoped>
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 999;
  cursor: pointer;
}

.drawer {
  position: fixed;
  top: 10px;
  z-index: 1000;
  background-color: white;
  box-shadow: var(--ion-box-shadow-hover);
  overflow-y: auto;
  border-radius: 3vmin 0 0 0;
}

.drawer--left {
  left: 0;
}

.drawer--right {
  right: 0;
}

/* Optional: Add scrollbar styling */
.drawer::-webkit-scrollbar {
  width: 6px;
}

.drawer::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.drawer::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.drawer::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
